import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
    Trash2, PlusCircle, Loader2, Home, 
    Check, Bed as BedIcon, Users, 
    DollarSign, ShieldCheck, LayoutDashboard,
    ChevronRight, ArrowRight
} from 'lucide-react';
import { toast } from 'react-hot-toast';

const WardBedSetup = () => {
    const [wards, setWards] = useState([]);
    const [practitioners, setPractitioners] = useState([]);
    const [selectedWardId, setSelectedWardId] = useState(null);
    const [loading, setLoading] = useState(true);

    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    const fetchAll = async () => {
        try {
            setLoading(true);
            const [wardsRes, pracRes] = await Promise.all([
                axios.get(`${window.location.protocol}//${window.location.hostname}/api/inpatient/wards`, { headers }),
                axios.get(`${window.location.protocol}//${window.location.hostname}/api/users/practitioners`, { headers })
            ]);
            setWards(wardsRes.data);
            setPractitioners(pracRes.data);
            if (wardsRes.data.length > 0 && !selectedWardId) {
                setSelectedWardId(wardsRes.data[0].id);
            }
        } catch (err) {
            toast.error('Failed to load setup data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAll();
    }, []);

    const handleUpdateWard = async (id, data) => {
        try {
            await axios.put(`http://localhost:5000/api/inpatient/wards/${id}`, data, { headers });
            toast.success('Ward updated');
            fetchAll();
        } catch (err) {
            toast.error('Update failed');
        }
    };

    const handleAddWard = async () => {
        try {
            const newWard = {
                name: 'New Ward Facility',
                capacity: 10,
                category: 'General',
                matronName: '',
                nurseInCharge: ''
            };
            const res = await axios.post(`${window.location.protocol}//${window.location.hostname}/api/inpatient/wards`, newWard, { headers });
            toast.success('Ward added');
            setSelectedWardId(res.data.id);
            fetchAll();
        } catch (err) {
            toast.error('Failed to add ward');
        }
    };

    const handleDeleteWard = async (id) => {
        if (!window.confirm('Delete this ward? all beds will be removed.')) return;
        try {
            await axios.delete(`http://localhost:5000/api/inpatient/wards/${id}`, { headers });
            toast.success('Ward deleted');
            if (selectedWardId === id) setSelectedWardId(null);
            fetchAll();
        } catch (err) {
            toast.error('Delete failed');
        }
    };

    const handleUpdateBed = async (id, data) => {
        try {
            await axios.put(`http://localhost:5000/api/inpatient/beds/${id}`, data, { headers });
            fetchAll();
        } catch (err) {
            toast.error('Bed update failed');
        }
    };

    const handleAddBed = async () => {
        if (!selectedWardId) return;
        try {
            const newBed = {
                bedNumber: 'B-' + Math.floor(Math.random() * 900 + 100),
                price: 150,
                wardId: selectedWardId
            };
            await axios.post(`${window.location.protocol}//${window.location.hostname}/api/inpatient/beds`, newBed, { headers });
            toast.success('Bed added');
            fetchAll();
        } catch (err) {
            toast.error('Failed to add bed');
        }
    };

    const handleDeleteBed = async (id) => {
        try {
            await axios.delete(`http://localhost:5000/api/inpatient/beds/${id}`, { headers });
            toast.success('Bed removed');
            fetchAll();
        } catch (err) {
            toast.error('Failed to delete bed');
        }
    };

    const activeWard = wards.find(w => w.id === selectedWardId);

    if (loading && wards.length === 0) return (
        <div className="flex items-center justify-center min-h-[400px]">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
    );

    return (
        <div className="p-6 md:p-8 bg-[#F8FAFC] min-h-screen text-slate-700 space-y-8 font-sans">
            <div className="max-w-7xl mx-auto space-y-10">
                
                {/* Header Pattern like ClinicSetup */}
                <div className="bg-white/80 backdrop-blur-md px-10 py-8 rounded-[40px] border border-slate-200 shadow-xl shadow-slate-200/40 flex justify-between items-center relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-full h-[3px] bg-gradient-to-r from-indigo-500 via-sky-500 to-indigo-500" />
                    <div className="flex items-center gap-6">
                        <div className="w-14 h-14 bg-indigo-600 rounded-2xl flex items-center justify-center shadow-lg shadow-indigo-200">
                            <Home className="w-7 h-7 text-white" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-black text-slate-800 tracking-tight">Facility Provisioning</h1>
                            <p className="text-[11px] text-slate-400 font-black uppercase tracking-widest mt-1">Configure Wards, Units and Bed Resources</p>
                        </div>
                    </div>
                    <button 
                        onClick={handleAddWard}
                        className="flex items-center gap-3 px-8 py-4 bg-slate-900 hover:bg-indigo-600 text-white rounded-3xl text-[12px] font-black uppercase tracking-widest shadow-xl transition-all active:scale-95 group"
                    >
                        <PlusCircle className="w-4 h-4 group-hover:rotate-90 transition-transform" />
                        Add Ward Facility
                    </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                    
                    {/* Wards List Column */}
                    <div className="lg:col-span-5 bg-white rounded-[40px] border border-slate-200 shadow-sm overflow-hidden p-3">
                         <div className="px-6 py-5 border-b border-slate-50 flex justify-between items-center bg-slate-50/50 rounded-t-[32px]">
                            <h2 className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Global Wards List</h2>
                            <span className="px-3 py-1 bg-white border border-slate-100 rounded-full text-[10px] font-black text-indigo-500">{wards.length}</span>
                         </div>
                         <div className="max-h-[600px] overflow-auto space-y-2 py-4 px-2 custom-scrollbar">
                            {wards.map(ward => (
                                <div 
                                    key={ward.id}
                                    onClick={() => setSelectedWardId(ward.id)}
                                    className={`p-6 rounded-[32px] cursor-pointer transition-all border flex justify-between items-center group ${
                                        selectedWardId === ward.id 
                                        ? 'bg-indigo-600 border-indigo-600 text-white shadow-xl shadow-indigo-200' 
                                        : 'bg-white border-transparent hover:border-slate-200 hover:bg-slate-50'
                                    }`}
                                >
                                    <div className="space-y-1">
                                        <h3 className={`text-sm font-black tracking-tight ${selectedWardId === ward.id ? 'text-white' : 'text-slate-800'}`}>
                                            {ward.name}
                                        </h3>
                                        <div className="flex items-center gap-3">
                                            <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-lg ${
                                                selectedWardId === ward.id ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500'
                                            }`}>
                                                {ward.category}
                                            </span>
                                            <span className={`text-[10px] font-bold ${selectedWardId === ward.id ? 'text-white/60' : 'text-slate-400'}`}>
                                                {ward.beds?.length || 0} Beds
                                            </span>
                                        </div>
                                    </div>
                                    <button 
                                        onClick={(e) => { e.stopPropagation(); handleDeleteWard(ward.id); }}
                                        className={`p-3 rounded-2xl transition-all ${
                                            selectedWardId === ward.id 
                                            ? 'text-white hover:bg-white/10' 
                                            : 'text-slate-200 hover:text-rose-500 hover:bg-rose-50'
                                        }`}
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            ))}
                         </div>
                    </div>

                    {/* Ward & Beds Detail Management Column */}
                    <div className="lg:col-span-7 space-y-8 animate-in slide-in-from-right-4 duration-500">
                        {activeWard ? (
                            <>
                                {/* Ward Property Setup Table */}
                                <div className="bg-white rounded-[44px] border border-slate-200 shadow-sm overflow-hidden p-8 space-y-8">
                                    <div className="flex justify-between items-center">
                                        <div className="flex items-center gap-4">
                                            <div className="w-12 h-12 bg-slate-900 rounded-2xl flex items-center justify-center text-white">
                                                <LayoutDashboard className="w-6 h-6" />
                                            </div>
                                            <div>
                                                <h2 className="text-xl font-black text-slate-800 tracking-tight leading-none">Ward Configuration</h2>
                                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-2 px-1">Global settings for {activeWard.name}</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-8">
                                        <div className="space-y-3">
                                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4">Facility Identity</label>
                                            <input 
                                                type="text"
                                                value={activeWard.name}
                                                onBlur={(e) => handleUpdateWard(activeWard.id, { name: e.target.value })}
                                                onChange={(e) => setWards(wards.map(w => w.id === activeWard.id ? {...w, name: e.target.value} : w))}
                                                className="w-full h-14 bg-slate-50 border border-slate-100 rounded-2xl px-6 text-sm font-bold text-slate-700 focus:outline-none focus:border-indigo-500 focus:bg-white transition-all shadow-sm"
                                            />
                                        </div>
                                        <div className="space-y-3">
                                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4">Matron In-Charge (Practitioner)</label>
                                            <select 
                                                value={activeWard.matronName}
                                                onChange={(e) => handleUpdateWard(activeWard.id, { matronName: e.target.value })}
                                                className="w-full h-14 bg-slate-50 border border-slate-100 rounded-2xl px-6 text-sm font-bold text-slate-700 outline-none focus:border-indigo-500 focus:bg-white transition-all appearance-none"
                                            >
                                                <option value="">Select Practitioner...</option>
                                                {practitioners.map(p => (
                                                    <option key={p.id} value={`${p.firstName} ${p.lastName}`}>{p.firstName} {p.lastName} ({p.role})</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="space-y-3">
                                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4">Unit Category</label>
                                            <select 
                                                value={activeWard.category}
                                                onChange={(e) => handleUpdateWard(activeWard.id, { category: e.target.value })}
                                                className="w-full h-14 bg-slate-50 border border-slate-100 rounded-2xl px-6 text-sm font-bold text-slate-700 outline-none transition-all appearance-none"
                                            >
                                                <option>General</option><option>Private</option><option>ICU</option><option>Pediatrics</option><option>Maternity</option><option>Mortuary</option>
                                            </select>
                                        </div>
                                        <div className="space-y-3">
                                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4">System Capacity</label>
                                            <input 
                                                type="number"
                                                value={activeWard.capacity}
                                                onBlur={(e) => handleUpdateWard(activeWard.id, { capacity: e.target.value })}
                                                onChange={(e) => setWards(wards.map(w => w.id === activeWard.id ? {...w, capacity: e.target.value} : w))}
                                                className="w-full h-14 bg-slate-50 border border-slate-100 rounded-2xl px-6 text-sm font-bold text-slate-700 outline-none transition-all shadow-sm"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Beds Resource Management Table */}
                                <div className="bg-white rounded-[44px] border border-slate-200 shadow-sm overflow-hidden flex flex-col">
                                    <div className="p-8 border-b border-slate-50 flex justify-between items-center">
                                        <div className="flex items-center gap-4">
                                            <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center text-indigo-600">
                                                <BedIcon className="w-5 h-5" />
                                            </div>
                                            <h2 className="text-lg font-black text-slate-800 tracking-tight">Resource Inventory</h2>
                                        </div>
                                        <button 
                                            onClick={handleAddBed}
                                            className="px-6 py-2.5 bg-slate-50 hover:bg-indigo-600 hover:text-white text-slate-600 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all active:scale-95 flex items-center gap-2"
                                        >
                                            <PlusCircle className="w-4 h-4" /> Provision New Bed
                                        </button>
                                    </div>

                                    <div className="overflow-x-auto">
                                        <table className="w-full text-left border-collapse">
                                            <thead className="bg-slate-50/50">
                                                <tr>
                                                    <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">Bed Identity</th>
                                                    <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center text-nowrap">Daily Rate</th>
                                                    <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Delete</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-50">
                                                {activeWard.beds?.map(bed => (
                                                    <tr key={bed.id} className="group/row hover:bg-slate-50/50 transition-all">
                                                        <td className="px-8 py-4">
                                                            <input 
                                                                type="text"
                                                                value={bed.bedNumber}
                                                                onBlur={(e) => handleUpdateBed(bed.id, { bedNumber: e.target.value })}
                                                                onChange={(e) => {
                                                                    const val = e.target.value;
                                                                    setWards(wards.map(w => w.id === activeWard.id ? {
                                                                        ...w, beds: w.beds.map(b => b.id === bed.id ? {...b, bedNumber: val} : b)
                                                                    } : w));
                                                                }}
                                                                className="w-full bg-transparent p-2 text-[14px] font-black text-slate-700 focus:outline-none focus:bg-white focus:ring-2 focus:ring-indigo-100 rounded-lg transition-all"
                                                            />
                                                        </td>
                                                        <td className="px-8 py-4">
                                                            <div className="flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-50 rounded-2xl group-hover/row:bg-white transition-all border border-transparent group-hover/row:border-slate-100 shadow-sm">
                                                                <DollarSign className="w-3.5 h-3.5 text-indigo-400" />
                                                                <input 
                                                                    type="number"
                                                                    value={bed.price}
                                                                    onBlur={(e) => handleUpdateBed(bed.id, { price: e.target.value })}
                                                                    onChange={(e) => {
                                                                        const val = e.target.value;
                                                                        setWards(wards.map(w => w.id === activeWard.id ? {
                                                                            ...w, beds: w.beds.map(b => b.id === bed.id ? {...b, price: val} : b)
                                                                        } : w));
                                                                    }}
                                                                    className="w-20 bg-transparent text-center text-xs font-black text-slate-800 focus:outline-none"
                                                                />
                                                            </div>
                                                        </td>
                                                        <td className="px-8 py-4 text-right">
                                                            <button 
                                                                onClick={() => handleDeleteBed(bed.id)}
                                                                className="p-3 text-slate-200 hover:text-rose-500 hover:bg-rose-50 rounded-xl transition-all opacity-0 group-hover/row:opacity-100"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        {(!activeWard.beds || activeWard.beds.length === 0) && (
                                            <div className="py-20 text-center space-y-4">
                                                <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto text-slate-200">
                                                    <BedIcon className="w-8 h-8" />
                                                </div>
                                                <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest">No resources provisioned for this ward</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="h-full flex items-center justify-center bg-white rounded-[44px] border border-dashed border-slate-200 p-20 text-center">
                                <div className="space-y-6 max-w-sm">
                                    <div className="w-24 h-24 bg-slate-50 rounded-[40px] flex items-center justify-center mx-auto text-slate-200">
                                        <ArrowRight className="w-12 h-12" />
                                    </div>
                                    <h2 className="text-xl font-black text-slate-300 tracking-tight uppercase tracking-widest">Select a Ward facility</h2>
                                    <p className="text-[11px] font-bold text-slate-400 leading-relaxed uppercase tracking-widest">Select a facility from the left panel to configure its clinical settings and bed resources.</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WardBedSetup;
